import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SpinnerGap, ShieldCheck } from "@phosphor-icons/react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ org_name: "", admin_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [totp, setTotp] = useState(null); // { enroll, challenge, provisioning_uri }
  const [code, setCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const { applyAuth } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finish = (data) => {
    applyAuth(data);
    toast.success("Welcome back");
    nav(data.user?.role === "superadmin" ? "/superadmin" : "/app");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(url, payload);
      if (data.status === "totp_required" || data.status === "totp_enrollment") {
        setTotp({ enroll: data.status === "totp_enrollment", challenge: data.challenge, provisioning_uri: data.provisioning_uri });
        setCode("");
        return;
      }
      if (data.status === "mfa_required") {
        setTotp({ tenant: true, challenge: data.challenge, emailAvailable: !!data.email_available });
        setCode(""); setRecoveryMode(false); setEmailMode(false);
        return;
      }
      applyAuth(data);
      toast.success(mode === "register" ? "Organisation created" : "Welcome back");
      nav(data.user?.role === "superadmin" ? "/superadmin" : "/app");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let url;
      if (totp.tenant) {
        url = emailMode ? "/auth/2fa/email-otp/verify" : recoveryMode ? "/auth/2fa/recovery" : "/auth/2fa/totp";
      } else {
        url = totp.enroll ? "/auth/superadmin/enroll" : recoveryMode ? "/auth/superadmin/recovery" : "/auth/superadmin/totp";
      }
      const { data } = await api.post(url, { challenge: totp.challenge, code });
      if (data.recovery_codes) { setRecoveryCodes(data.recovery_codes); setPendingData(data); return; }
      finish(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/email-otp/send", { challenge: totp.challenge });
      setEmailMode(true); setRecoveryMode(false); setCode("");
      toast.success(`Code sent to ${data.email_masked || "your email"}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (recoveryCodes) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-white grain">
        <div className="w-full max-w-md vf-fade-up" data-testid="recovery-codes-screen">
          <div className="mb-6"><Logo /></div>
          <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Save your recovery codes</h1>
          <p className="text-sm text-zinc-500 mb-5">Store these somewhere safe. Each code works <strong>once</strong> to sign in if you lose your authenticator. They won't be shown again.</p>
          <div className="grid grid-cols-2 gap-2 mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg font-mono text-sm">
            {recoveryCodes.map((rc) => <div key={rc} className="text-center py-1 tracking-widest">{rc}</div>)}
          </div>
          <button data-testid="copy-recovery" onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Copied"); }}
            className="w-full h-10 mb-3 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">Copy all</button>
          <button data-testid="recovery-continue" onClick={() => finish(pendingData)}
            className="w-full h-11 bg-black text-white rounded-md font-semibold hover:bg-zinc-800 transition-colors active:scale-[0.98]">I've saved them — continue</button>
        </div>
      </div>
    );
  }

  if (totp) {
    const enroll = totp.enroll;
    const title = enroll ? "Set up your authenticator"
      : emailMode ? "Enter the emailed code"
      : recoveryMode ? "Enter a recovery code" : "Enter your 6-digit code";
    const canSubmit = recoveryMode ? code.length >= 6 : code.length === 6;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-white grain">
        <div className="w-full max-w-sm vf-fade-up">
          <div className="mb-6"><Logo /></div>
          <div className="inline-flex items-center gap-2 text-[#1F6FE5] mb-3"><ShieldCheck size={22} weight="fill" /><span className="text-xs uppercase tracking-widest font-bold">Two-factor required</span></div>
          <h1 className="font-display font-bold text-2xl tracking-tight mb-1">{title}</h1>
          <p className="text-sm text-zinc-500 mb-6">
            {enroll ? "Scan this QR with Google Authenticator / Authy, then enter the code it shows."
              : emailMode ? "We emailed you a 6-digit sign-in code. It expires in 10 minutes."
              : recoveryMode ? "Enter one of your single-use recovery codes."
              : "Open your authenticator app and enter the current code."}
          </p>
          {enroll && totp.provisioning_uri && (
            <div className="flex justify-center mb-5" data-testid="totp-qr">
              <div className="bg-white p-4 border border-zinc-200 rounded-lg"><QRCodeCanvas value={totp.provisioning_uri} size={190} /></div>
            </div>
          )}
          <form onSubmit={submitTotp} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">{recoveryMode ? "Recovery code" : "Authenticator code"}</Label>
              {recoveryMode ? (
                <Input data-testid="recovery-code" autoComplete="one-time-code" maxLength={9}
                  value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                  placeholder="XXXX-XXXX" className="mt-1.5 tracking-widest font-mono text-center text-lg" />
              ) : (
                <Input data-testid="totp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000" className="mt-1.5 tracking-[0.4em] font-mono text-center text-lg" />
              )}
            </div>
            <button data-testid="totp-submit" disabled={loading || !canSubmit}
              className="w-full h-11 bg-black text-white rounded-md font-semibold hover:bg-zinc-800 transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <SpinnerGap size={18} className="animate-spin" />}
              {enroll ? "Verify & finish setup" : "Verify"}
            </button>
          </form>
          {!enroll && totp.tenant && (
            <div className="flex flex-col items-center gap-1.5 mt-4">
              {totp.emailAvailable && !emailMode && (
                <button data-testid="mfa-email-otp" onClick={sendEmailOtp} disabled={loading} className="text-xs text-[#1F6FE5] font-medium hover:underline disabled:opacity-50">
                  Can't use your app? Email me a code
                </button>
              )}
              {emailMode && (
                <button data-testid="mfa-back-to-app" onClick={() => { setEmailMode(false); setCode(""); }} className="text-xs text-[#1F6FE5] font-medium hover:underline">
                  Use your authenticator app instead
                </button>
              )}
              {!emailMode && (
                <button data-testid="toggle-recovery" onClick={() => { setRecoveryMode(!recoveryMode); setCode(""); }} className="text-xs text-[#1F6FE5] font-medium hover:underline">
                  {recoveryMode ? "Use your authenticator app instead" : "Lost your device? Use a recovery code"}
                </button>
              )}
            </div>
          )}
          {!enroll && !totp.tenant && (
            <p className="text-center mt-4">
              <button data-testid="toggle-recovery" onClick={() => { setRecoveryMode(!recoveryMode); setCode(""); }} className="text-xs text-[#1F6FE5] font-medium hover:underline">
                {recoveryMode ? "Use your authenticator app instead" : "Lost your device? Use a recovery code"}
              </button>
            </p>
          )}
          <p className="text-center mt-6">
            <button data-testid="totp-cancel" onClick={() => { setTotp(null); setCode(""); setRecoveryMode(false); setEmailMode(false); }} className="text-xs text-zinc-400 hover:text-black font-mono">← BACK TO SIGN IN</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain">
      <div className="hidden lg:flex flex-col justify-between bg-[#09090B] text-white p-12 relative z-10">
        <Logo dark height={30} />
        <div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-tight mb-4">
            Secure, anonymous,<br />verifiable voting.
          </h2>
          <p className="text-zinc-400 text-sm max-w-sm leading-relaxed">
            Ballots that carry no identity. One-time links. Atomic double-vote prevention. Built for organisations that can't afford to get it wrong.
          </p>
        </div>
        <p className="font-mono text-xs text-zinc-600">SELECTFLOW · MULTI-TENANT ELECTION PLATFORM</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white relative z-10">
        <div className="w-full max-w-sm vf-fade-up">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-1">
            {mode === "register" ? "Create your organisation" : "Sign in"}
          </h1>
          <p className="text-sm text-zinc-500 mb-8">
            {mode === "register" ? "Start running elections in minutes." : "Access your SelectFlow dashboard."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Organisation name</Label>
                  <Input data-testid="reg-org" required value={form.org_name} onChange={set("org_name")} placeholder="Acme Union" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Your name</Label>
                  <Input data-testid="reg-name" required value={form.admin_name} onChange={set("admin_name")} placeholder="Jane Admin" className="mt-1.5" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@org.com" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" className="mt-1.5" />
            </div>
            <button
              data-testid="auth-submit"
              disabled={loading}
              className="w-full h-11 bg-black text-white rounded-md font-semibold hover:bg-zinc-800 transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <SpinnerGap size={18} className="animate-spin" />}
              {mode === "register" ? "Create organisation" : "Sign in"}
            </button>
          </form>

          <p className="text-sm text-zinc-500 mt-6 text-center">
            {mode === "register" ? "Already have an account? " : "New to SelectFlow? "}
            <button
              data-testid="auth-toggle"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-[#1F6FE5] font-medium hover:underline"
            >
              {mode === "register" ? "Sign in" : "Create an organisation"}
            </button>
          </p>
          <p className="text-center mt-6">
            <Link to="/" className="text-xs text-zinc-400 hover:text-black font-mono">← BACK TO HOME</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
